// BuyGuide — compact 4-step horizontal timeline guiding the user to the
// in-page SwapWidget. Replaces the old large "Cómo comprar R₿L" section.
import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";

const T = {
  es: {
    heading: "🚀 Cómo comprar R₿L en menos de 2 minutos",
    steps: [
      { number: "01", icon: "👛", title: "Instala wallet", desc: "MetaMask, Rabby o Coinbase" },
      { number: "02", icon: "🔗", title: "Añade Base L2", desc: "Red de bajas comisiones" },
      { number: "03", icon: "⚡", title: "Consigue ETH", desc: "Desde cualquier exchange" },
      { number: "04", icon: "🔄", title: "Haz el swap", desc: "↓ Usa el widget de abajo" },
    ],
  },
  en: {
    heading: "🚀 How to buy R₿L in less than 2 minutes",
    steps: [
      { number: "01", icon: "👛", title: "Install wallet", desc: "MetaMask, Rabby or Coinbase" },
      { number: "02", icon: "🔗", title: "Add Base L2", desc: "Low fee network" },
      { number: "03", icon: "⚡", title: "Get ETH", desc: "From any exchange" },
      { number: "04", icon: "🔄", title: "Swap now", desc: "↓ Use the widget below" },
    ],
  },
  sk: {
    heading: "🚀 Ako kúpiť R₿L za menej ako 2 minúty",
    steps: [
      { number: "01", icon: "👛", title: "Inštaluj peňaženku", desc: "MetaMask, Rabby alebo Coinbase" },
      { number: "02", icon: "🔗", title: "Pridaj Base L2", desc: "Sieť s nízkymi poplatkami" },
      { number: "03", icon: "⚡", title: "Získaj ETH", desc: "Z akejkoľvek burzy" },
      { number: "04", icon: "🔄", title: "Vymeň teraz", desc: "↓ Použi widget nižšie" },
    ],
  }, de: { heading: "🚀 Wie man R₿L in weniger als 2 Minuten kauft", steps: [{ number: "01", icon: "👛", title: "Wallet installieren", desc: "MetaMask, Rabby oder Coinbase" }, { number: "02", icon: "🔗", title: "Base L2 hinzufügen", desc: "Netzwerk mit niedrigen Gebühren" }, { number: "03", icon: "⚡", title: "ETH besorgen", desc: "Von jeder Exchange" }, { number: "04", icon: "🔄", title: "Swap durchführen", desc: "↓ Nutze das Widget unten" }] }, "zh-CN": { heading: "🚀 如何在2分钟内购买 R₿L", steps: [{ number: "01", icon: "👛", title: "安装钱包", desc: "MetaMask、Rabby 或 Coinbase" }, { number: "02", icon: "🔗", title: "添加 Base L2", desc: "低手续费网络" }, { number: "03", icon: "⚡", title: "获取 ETH", desc: "从任何交易所" }, { number: "04", icon: "🔄", title: "进行兑换", desc: "↓ 使用下方小工具" }] },
};

export default function BuyGuide({ onScrollToSwap }) {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      data-testid="buy-guide"
      className="mx-auto w-full max-w-3xl rounded-2xl backdrop-blur-sm"
      style={{
        background: "rgba(10,10,15,0.8)",
        border: "1px solid rgba(109,40,217,0.3)",
        padding: "20px 24px",
      }}
    >
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="mb-4 text-sm font-semibold text-white"
        data-testid="buy-guide-heading"
      >
        {t.heading}
      </motion.p>

      <div className="relative flex flex-col items-stretch gap-4 md:flex-row md:items-start md:justify-between md:gap-2">
        {/* Connector line — horizontal on desktop, vertical on mobile */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="absolute left-0 right-0 top-[30px] hidden h-[2px] origin-left md:block"
          style={{ background: "linear-gradient(90deg,#6d28d9,#f97316)" }}
        />
        <motion.div
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="absolute bottom-4 left-[30px] top-4 w-[2px] origin-top md:hidden"
          style={{ background: "linear-gradient(180deg,#6d28d9,#f97316)" }}
        />

        {t.steps.map((s, i) => {
          const isLast = i === t.steps.length - 1;
          return (
            <motion.div
              key={s.number}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 0.15 + i * 0.1 }}
              whileHover={{ scale: 1.05 }}
              onClick={isLast ? onScrollToSwap : undefined}
              data-testid={`buy-guide-step-${i + 1}`}
              className={`group relative z-10 flex flex-row items-center gap-3 rounded-xl border border-transparent p-2 transition-colors duration-200 hover:border-[#f97316]/60 md:flex-1 md:flex-col md:gap-1.5 md:text-center ${isLast ? "cursor-pointer" : ""}`}
            >
              <div
                className="relative flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full text-2xl"
                style={{
                  background: "#111118",
                  border: `2px solid ${isLast ? "#f97316" : "rgba(109,40,217,0.6)"}`,
                  ...(isLast ? { animation: "buyguide-pulse 2s ease-in-out infinite" } : {}),
                }}
              >
                <span className="transition-transform duration-200 group-hover:rotate-12 group-hover:scale-110">{s.icon}</span>
                <span
                  className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-black text-white"
                  style={{ background: "#6d28d9" }}
                >
                  {s.number}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white">{s.title}</p>
                <p className="text-[10px] leading-tight" style={{ color: "#9ca3af" }}>
                  {isLast ? (
                    <span className="inline-flex items-center gap-1 font-bold" style={{ color: "#f97316" }}>
                      <motion.span
                        animate={{ y: [0, 3, 0] }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                        className="inline-block"
                      >
                        ↓
                      </motion.span>
                      {s.desc.replace("↓ ", "")}
                    </span>
                  ) : (
                    s.desc
                  )}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <style>{`
        @keyframes buyguide-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.45); }
          50% { box-shadow: 0 0 0 7px rgba(249,115,22,0); }
        }
      `}</style>
    </motion.div>
  );
}
